import React, { useState, useCallback, useMemo } from "react";
import { Pressable, View, ActivityIndicator } from "react-native";
import { Text } from "@src/packages/ui/Text/Text";
import {
  DynamicModal,
  DynamicModalHeader,
  DynamicModalContent,
  DynamicModalFooter,
} from "@src/packages/ui/DynamicModal/DynamicModal";
import {
  Calendar,
  Check,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react-native";
import { useLogPeriod, type LogPeriodPayload } from "../useCalendarData";
import { toLocalDateString } from "@src/packages/utils/date";
import { formatDate as formatLocalizedDate } from "@src/packages/i18n/formatters";
import { useLocale } from "@src/packages/i18n/useLocale";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type FlowLevel = "spotting" | "light" | "medium" | "heavy";

interface LogPeriodModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional: Pre-select a date (defaults to today) */
  initialDate?: Date;
  /** Callback when period is successfully logged */
  onPeriodLogged?: (payload: LogPeriodPayload) => void;
}

const FLOW_OPTIONS: {
  value: FlowLevel;
  label: string;
  size: number;
  opacity: number;
}[] = [
  { value: "spotting", label: "Spotting", size: 8, opacity: 0.4 },
  { value: "light", label: "Light", size: 12, opacity: 0.6 },
  { value: "medium", label: "Medium", size: 16, opacity: 0.8 },
  { value: "heavy", label: "Heavy", size: 20, opacity: 1 },
];

export function LogPeriodModal({
  open,
  onClose,
  initialDate,
  onPeriodLogged,
}: LogPeriodModalProps) {
  const { locale } = useLocale();
  const capturePress = usePressCapture("LogPeriodModal");
  const [selectedFlow, setSelectedFlow] = useState<FlowLevel>("medium");
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialDate ?? new Date(),
  );
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<
    "success" | "warning" | "error"
  >("success");

  // Use the period logging mutation
  const logPeriodMutation = useLogPeriod();

  // Date helper functions - memoize today to prevent useCallback deps from changing on every render
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isToday = useCallback(
    (date: Date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    },
    [today],
  );

  const isYesterday = useCallback(
    (date: Date) => {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === yesterday.getTime();
    },
    [today],
  );

  const formatDate = (date: Date) => {
    const formatted = formatLocalizedDate(date, locale, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    if (isToday(date)) {
      return `Today, ${formatLocalizedDate(date, locale, { month: "short", day: "numeric" })}`;
    }
    if (isYesterday(date)) {
      return `Yesterday, ${formatLocalizedDate(date, locale, { month: "short", day: "numeric" })}`;
    }
    return formatted;
  };

  const goToPreviousDay = useCallback(() => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  }, []);

  const goToNextDay = useCallback(() => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      // Don't go beyond today
      if (newDate > today) return prev;
      return newDate;
    });
  }, [today]);

  const canGoForward = !isToday(selectedDate);

  const handleSave = useCallback(async () => {
    // Clear any previous feedback
    setFeedbackMessage(null);

    const payload: LogPeriodPayload = {
      date: toLocalDateString(selectedDate),
      flowLevel: selectedFlow,
      notes: null,
    };

    try {
      const result = await logPeriodMutation.mutateAsync(payload);

      if (result.success) {
        // Successfully persisted
        setFeedbackType("success");
        setFeedbackMessage(result.message);

        // Notify parent component
        onPeriodLogged?.(payload);

        // Close modal after a short delay to show success message
        setTimeout(() => {
          onClose();
          setFeedbackMessage(null);
        }, 500);
      } else {
        // Data not persisted (API not available)
        setFeedbackType("warning");
        setFeedbackMessage(result.message);

        // Still notify parent and close after showing warning
        onPeriodLogged?.(payload);
        setTimeout(() => {
          onClose();
          setFeedbackMessage(null);
        }, 2000);
      }
    } catch (error) {
      console.error("[LogPeriodModal] Error logging period:", error);
      setFeedbackType("error");
      setFeedbackMessage(
        error instanceof Error
          ? error.message
          : "Failed to log period. Please try again.",
      );
    }
  }, [selectedDate, selectedFlow, logPeriodMutation, onPeriodLogged, onClose]);

  const handleClose = useCallback(() => {
    // Reset state on close
    setSelectedFlow("medium");
    setSelectedDate(initialDate ?? new Date());
    setFeedbackMessage(null);
    onClose();
  }, [initialDate, onClose]);

  const isLoading = logPeriodMutation.isPending;

  return (
    <DynamicModal open={open} onClose={handleClose}>
      <DynamicModalHeader>
        <View className="flex-row items-center justify-between">
          <View>
            <Text
              variant="title"
              size="lg"
              className="text-xl font-medium text-white"
              style={{ fontFamily: "Tobias-Thin" }}
            >
              Log Period
            </Text>
            <Text
              variant="body"
              size="sm"
              className="mt-1 text-xs text-slate-400"
            >
              How is your flow today?
            </Text>
          </View>
          <Pressable
            onPress={() => {
              capturePress("Close modal");
              handleClose();
            }}
            disabled={isLoading}
            className="-mr-2 rounded-full p-2"
            accessibilityRole="button"
            accessibilityLabel="Close modal"
          >
            <X size={20} color="#94A3B8" />
          </Pressable>
        </View>
      </DynamicModalHeader>

      <DynamicModalContent>
        <View className="space-y-6">
          {/* Feedback Message */}
          {feedbackMessage && (
            <View
              className={`flex-row items-center gap-2 rounded-lg p-3 ${
                feedbackType === "success"
                  ? "border border-green-500/20 bg-green-500/10"
                  : feedbackType === "warning"
                    ? "border border-amber-500/20 bg-amber-500/10"
                    : "border border-red-500/20 bg-red-500/10"
              }`}
            >
              <AlertCircle
                size={16}
                color={
                  feedbackType === "success"
                    ? "#4ADE80"
                    : feedbackType === "warning"
                      ? "#FBBF24"
                      : "#F87171"
                }
              />
              <Text
                className={`flex-1 text-xs ${
                  feedbackType === "success"
                    ? "text-green-400"
                    : feedbackType === "warning"
                      ? "text-amber-400"
                      : "text-red-400"
                }`}
              >
                {feedbackMessage}
              </Text>
            </View>
          )}

          {/* Flow Selection */}
          <View className="flex-row gap-2">
            {FLOW_OPTIONS.map((option) => {
              const isSelected = selectedFlow === option.value;
              return (
                <Pressable
                  key={option.value}
                  onPress={() => {
                    capturePress(pressLabel(option.label), "flow_level_option");
                    setSelectedFlow(option.value);
                  }}
                  disabled={isLoading}
                  className={`flex-1 flex-col items-center justify-center gap-2 rounded-xl border p-3 ${
                    isSelected
                      ? "border-[#C4B5FD] bg-[#C4B5FD]/20"
                      : "border-white/10 bg-white/5"
                  } ${isLoading ? "opacity-50" : ""}`}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isSelected }}
                >
                  <View
                    style={{
                      width: option.size,
                      height: option.size,
                      borderRadius: option.size / 2,
                      backgroundColor: "#C4B5FD",
                      opacity: isSelected ? 1 : option.opacity,
                      shadowColor: "#C4B5FD",
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: isSelected ? 0.5 : 0,
                      shadowRadius: 10,
                    }}
                  />
                  <Text
                    variant="caption"
                    className={`text-[0.65rem] font-medium ${
                      isSelected ? "text-white" : "text-slate-400"
                    }`}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          {/* Date Stepper */}
          <View className="flex-row items-center justify-between rounded-xl border border-white/10 bg-white/5 p-2">
            {/* Previous Day Button */}
            <Pressable
              onPress={() => {
                capturePress("Previous day", "previous_day");
                goToPreviousDay();
              }}
              disabled={isLoading}
              className={`rounded-lg p-3 ${isLoading ? "opacity-50" : "active:bg-white/10"}`}
              accessibilityRole="button"
              accessibilityLabel="Previous day"
            >
              <ChevronLeft size={20} color="#C4B5FD" />
            </Pressable>

            {/* Date Display */}
            <View className="flex-1 flex-row items-center justify-center gap-2">
              <Calendar size={16} color="#C4B5FD" />
              <Text
                variant="body"
                size="sm"
                className="text-sm font-semibold text-white"
              >
                {formatDate(selectedDate)}
              </Text>
            </View>

            {/* Next Day Button */}
            <Pressable
              onPress={() => {
                capturePress("Next day", "next_day");
                goToNextDay();
              }}
              disabled={isLoading || !canGoForward}
              className={`rounded-lg p-3 ${isLoading || !canGoForward ? "opacity-30" : "active:bg-white/10"}`}
              accessibilityRole="button"
              accessibilityLabel="Next day"
            >
              <ChevronRight size={20} color="#C4B5FD" />
            </Pressable>
          </View>
        </View>
      </DynamicModalContent>

      <DynamicModalFooter>
        <Pressable
          onPress={() => {
            capturePress("Save Log", "save_period_log");
            void handleSave();
          }}
          disabled={isLoading}
          className={`w-full flex-row items-center justify-center gap-2 rounded-xl py-4 ${
            isLoading ? "bg-[#C4B5FD]/50" : "bg-[#C4B5FD]"
          }`}
          style={{
            shadowColor: "#C4B5FD",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 20,
          }}
          accessibilityRole="button"
          accessibilityLabel={
            isLoading ? "Saving period log" : "Save period log"
          }
        >
          {isLoading ? (
            <>
              <ActivityIndicator size="small" color="#0F172A" />
              <Text
                variant="body"
                size="md"
                className="font-bold text-[#0F172A]"
              >
                Saving...
              </Text>
            </>
          ) : (
            <>
              <Check size={18} color="#0F172A" strokeWidth={2.5} />
              <Text
                variant="body"
                size="md"
                className="font-bold text-[#0F172A]"
              >
                Save Log
              </Text>
            </>
          )}
        </Pressable>
      </DynamicModalFooter>
    </DynamicModal>
  );
}
